import './index.less';

import { Button, ConfigProvider, Input, Modal, Select, Space, Table, Tag } from 'antd';
import startCase from 'lodash.startcase';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { createBreakpoint, useLifecycles } from 'react-use';

import { PageHeader } from '@ant-design/pro-components';

import { disbursementAgreementsStateReset } from '../../../../features/disbursement/disbursementSlice';
import { numberWithCommas } from '../../../../helpers/number';
import {
  getDisbursementAgreementsByBatchIdTrunk,
  getDisbursementBatchDetailsTrunk,
} from '../../../../services/disbursementService/disbursementTrunk';
import { AppDispatch, RootState } from '../../../../store/store';
import { onCalculateNetDisbursementAmount } from '../../../../utils/calculation.util';
import { displayDate } from '../../../../utils/datetime.util';

import type { ColumnsType } from 'antd/es/table';
import type { TablePaginationConfig } from 'antd/es/table';
import UpdateDisbursementModal from './UpdateDisbursementModal';

const { Option } = Select;
const { Search } = Input;

const useBreakpoint = createBreakpoint();

const BatchDetailsPage = () => {
  const [tableLoading, setTableLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'tablet';

  const { disbursementAgreements, disbursementBatchDetails } = useSelector(
    (state: RootState) => state.disbursement
  );

  const { batchId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const pageNum = queryParams.get('pageNum');
  const pageSize = queryParams.get('pageSize');

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: pageNum ? +pageNum : 1,
        pageSize: pageSize ? +pageSize : 20,
      },
      sortOrder: 'desc',
      sortField: 'createdAt',
      search: {},
      filters: {},
    }),
    []
  );

  const [{ confirm }, contextHolder] = Modal.useModal();
  const [filterState, setFilterState] = useState(initialParams.filters);
  const [paginationState, setPaginationState] = useState(initialParams.pagination);
  const [searchFiled, setSearchFiled] = useState('agreementNo');

  const tablePagination = {
    total: disbursementAgreements?.data?.total,
    showSizeChanger: true,
    showTotal: (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} items`,
    current: paginationState.current,
    pageSize: paginationState.pageSize,
  };

  const openInProcessTableColumns: ColumnsType<any> = [
    {
      title: 'Signed Agreement Date',
      dataIndex: 'signDate',
      key: 'signDate',
      render: (text) => (text ? displayDate(text) : '-'),
    },
    {
      title: 'Agreement No',
      dataIndex: 'agreementNo',
      key: 'agreementNo',
      render: (text, record) => (
        <Button type="link" onClick={() => navigate(`/agreement/details/${record.id}`)}>
          <span style={{ userSelect: 'text' }}>{text}</span>
        </Button>
      ),
    },
    {
      title: 'Applicant Name',
      dataIndex: 'user',
      key: 'user',
      render: (_, record) =>
        record.application?.applicantFirstName ? record.application?.applicantFirstName : '-',
    },
    {
      title: 'Applicant NRIC',
      dataIndex: 'user',
      key: 'user',
      render: (_, record) =>
        record.application?.applicantNric ? record.application?.applicantNric : '-',
    },
    {
      title: 'Approved Loan Amount',
      dataIndex: 'systemLoanAmountSuggested',
      key: 'systemLoanAmountSuggested',
      render: (_, record) =>
        record.application?.financeAmount != null
          ? `RM ${numberWithCommas(record.application?.financeAmount ?? 0)}`
          : '-',
    },
    {
      title: 'Net Disbursement Amount',
      dataIndex: 'disbursementAmount',
      key: 'disbursementAmount',
      render: (text, record) =>
        `RM ${numberWithCommas(
          onCalculateNetDisbursementAmount(
            record.application?.financeAmount ?? 0,
            record.application?.productProcessingFee ?? 0,
            record.application?.productStampingDutyPercentage ?? 0,
            record.application?.productProcessingFeeToDiscount ?? 0,
            record.application?.loanTotalPremium ?? 0,
            record.application?.productWakalahFee,
            record.application?.applicationStatus,
            record.application?.applicationType
          )
        )}`,
    },
    {
      title: 'Update',
      key: 'update',
      render: (text, record) => (
        <>
          <UpdateDisbursementModal
            key={record.id}
            batchId={batchId}
            agreement={record}
            callback={updateDisbursementCallback}
          />
        </>
      ),
    },
  ];

  const completedTableColumns: ColumnsType<any> = [
    {
      title: 'Signed Agreement Date',
      dataIndex: 'signDate',
      key: 'signDate',
      render: (text) => (text ? displayDate(text) : '-'),
    },
    {
      title: 'Agreement No',
      dataIndex: 'agreementNo',
      key: 'agreementNo',
      render: (text, record) => (
        <Button type="link" onClick={() => navigate(`/agreement/details/${record.id}`)}>
          <span style={{ userSelect: 'text' }}>{text}</span>
        </Button>
      ),
    },
    {
      title: 'Applicant Name',
      dataIndex: 'user',
      key: 'user',
      render: (_, record) =>
        record.application?.applicantFirstName ? record.application?.applicantFirstName : '-',
    },
    {
      title: 'Applicant NRIC',
      dataIndex: 'user',
      key: 'user',
      render: (_, record) =>
        record.application?.applicantNric ? record.application?.applicantNric : '-',
    },
    {
      title: 'Approved Loan Amount',
      dataIndex: 'systemLoanAmountSuggested',
      key: 'systemLoanAmountSuggested',
      render: (_, record) =>
        record.application?.financeAmount != null
          ? `RM ${numberWithCommas(record.application?.financeAmount ?? 0)}`
          : '-',
    },
    {
      title: 'Net Disbursement Amount',
      dataIndex: 'disbursementAmount',
      key: 'disbursementAmount',
      render: (_, record) =>
        `RM ${numberWithCommas(
          onCalculateNetDisbursementAmount(
            record.application?.financeAmount ?? 0,
            record.application?.productProcessingFee ?? 0,
            record.application?.productStampingDutyPercentage ?? 0,
            record.application?.productProcessingFeeToDiscount ?? 0,
            record.application?.loanTotalPremium ?? 0,
            record.application?.productWakalahFee,
            record.application?.applicationStatus,
            record.application?.applicationType
          )
        )}`,
    },
    {
      title: 'Status',
      dataIndex: 'agreementStatus',
      key: 'agreementStatus',
    },
    {
      title: 'Posted Date',
      dataIndex: 'disbursementPostedDate',
      key: 'postedDate',
      render: (text) => (text ? displayDate(text) : '-'),
    },
  ];

  const incompleteTableColumns: ColumnsType<any> = [
    {
      title: 'Signed Agreement Date',
      dataIndex: 'signDate',
      key: 'signDate',
      render: (text) => (text ? displayDate(text) : '-'),
    },
    {
      title: 'Agreement No',
      dataIndex: 'agreementNo',
      key: 'agreementNo',
      render: (text, record) => (
        <Button type="link" onClick={() => navigate(`/agreement/details/${record.id}`)}>
          <span style={{ userSelect: 'text' }}>{text}</span>
        </Button>
      ),
    },
    {
      title: 'Applicant Name',
      dataIndex: 'user',
      key: 'user',
      render: (text, record) =>
        record.application?.applicantFirstName ? record.application?.applicantFirstName : '-',
    },
    {
      title: 'Applicant NRIC',
      dataIndex: 'user',
      key: 'user',
      render: (text, record) =>
        record.application?.applicantNric ? record.application?.applicantNric : '-',
    },
    {
      title: 'Approved Loan Amount',
      dataIndex: 'systemLoanAmountSuggested',
      key: 'systemLoanAmountSuggested',
      render: (text, record) => `RM ${numberWithCommas(record.application?.financeAmount)}`,
    },
    {
      title: 'Net Disbursement Amount',
      dataIndex: 'disbursementAmount',
      key: 'disbursementAmount',
      render: (text, record) =>
        `RM ${numberWithCommas(
          onCalculateNetDisbursementAmount(
            record.application?.financeAmount ?? 0,
            record.application?.productProcessingFee ?? 0,
            record.application?.productStampingDutyPercentage ?? 0,
            record.application?.productProcessingFeeToDiscount ?? 0,
            record.application?.loanTotalPremium ?? 0,
            record.application?.productWakalahFee,
            record.application?.applicationStatus,
            record.application?.applicationType
          )
        )}`,
    },
    {
      title: 'Status',
      dataIndex: 'agreementStatus',
      key: 'agreementStatus',
    },
    {
      title: 'Posted Date',
      dataIndex: 'disbursementPostedDate',
      key: 'postedDate',
      render: (text) => (text ? displayDate(text) : '-'),
    },
  ];

  const fetchDisbursementAgreements = async (params?: any) => {
    await dispatch(
      getDisbursementAgreementsByBatchIdTrunk({
        params,
        batchId: batchId,
      })
    );
  };

  const fetchDisbursementBatchDetails = async (batchId?: string) => {
    await dispatch(getDisbursementBatchDetailsTrunk({ batchId }));
  };

  const updateDisbursementCallback = async () => {
    await fetchDisbursementBatchDetails(batchId);
    await fetchDisbursementAgreements({
      ...initialParams,
      ...filterState,
    });
  };

  const onSearch = (value: string) => {
    value = value.trim();
    fetchDisbursementAgreements({
      ...initialParams,
      filters: { ...filterState, [searchFiled]: value },
    });
    setFilterState({ ...initialParams.filters, [searchFiled]: value });
  };

  const handleTableChange = async (pagination: TablePaginationConfig) => {
    fetchDisbursementAgreements({
      ...initialParams,
      ...filterState,
      pagination,
    });
    setPaginationState({
      current: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 10,
    });
  };

  useLifecycles(
    () => {
      fetchDisbursementAgreements({
        ...initialParams,
        ...filterState,
      });
      fetchDisbursementBatchDetails(batchId);
    },
    () => {
      dispatch(disbursementAgreementsStateReset());
    }
  );

  return (
    <>
      {contextHolder}
      <Helmet>
        <title>Disbursement Details - redCASH CEP</title>
      </Helmet>

      <PageHeader
        className="px-0 mb-2"
        title="Disbursement Details"
        onBack={() => navigate(-1)}
        subTitle={
          <Space size="middle">
            <span>{`Batch No. ${disbursementBatchDetails?.data?.batchNo}`}</span>
            {disbursementBatchDetails?.data?.batchNo && (
              <>
                <Tag
                  color={
                    disbursementBatchDetails?.data?.status === 'IN_COMPLETE'
                      ? 'error'
                      : disbursementBatchDetails?.data?.status === 'COMPLETED'
                      ? 'success'
                      : 'default'
                  }
                >
                  {startCase(
                    disbursementBatchDetails?.data?.status === 'IN_COMPLETE'
                      ? 'INCOMPLETE'
                      : disbursementBatchDetails?.data?.status
                  )}
                </Tag>
              </>
            )}
          </Space>
        }
      />

      <div className="flex justify-between mb-4">
        <Input.Group compact>
          <Select
            defaultValue="agreementNo"
            style={{ width: 160 }}
            onChange={(value) => setSearchFiled(value)}
          >
            <Option key="agreementNo" value="agreementNo">
              Agreement No.
            </Option>
            <Option key="applicationName" value="applicationName">
              Application Name
            </Option>
            <Option key="applicantIdNo" value="applicantIdNo">
              Applicant NRIC
            </Option>
          </Select>
          <Search placeholder="input search text" onSearch={onSearch} style={{ width: 240 }} />
        </Input.Group>
      </div>

      <ConfigProvider theme={{ token: { colorPrimary: '#8F8F8F' } }}>
        <Table
          rowKey="id"
          className="min-w-fit"
          columns={
            disbursementBatchDetails?.data?.status === 'IN_COMPLETE'
              ? incompleteTableColumns
              : disbursementBatchDetails?.data?.status === 'COMPLETED'
              ? completedTableColumns
              : openInProcessTableColumns
          }
          pagination={tablePagination}
          dataSource={disbursementAgreements?.data?.data as any}
          loading={disbursementAgreements?.isLoading || tableLoading}
          onChange={handleTableChange}
        />
      </ConfigProvider>
    </>
  );
};

export default BatchDetailsPage;
